import React, { useState } from "react";
import {
  Box,
  Flex,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  IconButton,
  Avatar,
  Tag,
  Divider,
} from "@chakra-ui/react";
import { FaArrowRight, FaEnvelope, FaStar, FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";

interface StrategistStats {
  portfoliosBuilt: number;
  avgEquityGain: number; // AUD
  marketsCovered: number;
}

interface Testimonial {
  id: string;
  initials: string;
  quote: string;
  attribution: string;
}

interface Strategist {
  id: string;
  name: string;
  title: string;
  location: string;
  yearsExperience: number;
  bio: string;
  photoUrl?: string;
  rating: number;
  reviewCount: number;
  specialisations: string[];
  credentials: string[];
  bookingUrl?: string;
  email?: string;
  stats: StrategistStats;
  testimonials: Testimonial[];
}

const STRATEGISTS: Strategist[] = [
  {
    id: "mark-mcevoy",
    name: "Mark McEvoy",
    title: "Senior Property Strategist",
    location: "Brisbane, QLD",
    yearsExperience: 11,
    bio: "Mark leads property strategy for high-income earners looking to build meaningful wealth outside of superannuation. With a background in financial planning and residential investment, he specialises in structuring portfolios that deliver strong rental yield alongside maximum depreciation and tax benefit. Mark has guided clients through acquisitions across South East Queensland, Northern NSW, and metro Melbourne.",
    rating: 4.9,
    reviewCount: 38,
    specialisations: ["Portfolio structuring", "Negative gearing strategies", "SMSF property acquisition", "Solar value-add advisory", "Dual-income household strategy"],
    credentials: ["Cert IV in Property Services", "Diploma of Financial Planning", "One Group Certified Property Strategist"],
    bookingUrl: "/property/consultation",
    email: "mark.mcevoy@onegroup.com.au",
    stats: { portfoliosBuilt: 64, avgEquityGain: 187000, marketsCovered: 9 },
    testimonials: [
      {
        id: "t1",
        initials: "JT",
        quote: "Mark walked us through every option and never pushed us toward a property we weren't comfortable with. Two years on, our portfolio is exactly on the track he mapped out. (placeholder)",
        attribution: "James T., Brisbane",
      },
      {
        id: "t2",
        initials: "SK",
        quote: "We came in knowing nothing about SMSF property and left with a clear, fully structured plan. (placeholder)",
        attribution: "Sarah K., Gold Coast",
      },
    ],
  },
  {
    id: "ryan-lee",
    name: "Ryan Lee",
    title: "Property Investment Strategist",
    location: "Gold Coast, QLD",
    yearsExperience: 8,
    bio: "Ryan combines deep data analysis with on-the-ground market knowledge to pinpoint investment opportunities before they peak. He focuses on growth corridor identification, rental demand forecasting, and solar value-add timing to maximise total return for clients. Ryan works closely with the finance team to ensure every acquisition is structured for long-term serviceability.",
    rating: 4.8,
    reviewCount: 25,
    specialisations: ["Growth corridor analysis", "Rental demand forecasting", "Buy-and-hold strategy", "Solar ROI integration", "First investment property coaching"],
    credentials: ["Bachelor of Property Economics", "Cert IV in Property Services", "One Group Certified Property Strategist"],
    bookingUrl: "/property/consultation",
    email: "ryan.lee@onegroup.com.au",
    stats: { portfoliosBuilt: 41, avgEquityGain: 142000, marketsCovered: 7 },
    testimonials: [
      {
        id: "t1",
        initials: "DM",
        quote: "Ryan's market data made the decision easy — we knew exactly why we were buying where we were buying. (placeholder)",
        attribution: "David M., Sunshine Coast",
      },
    ],
  },
  {
    id: "chris-spelson",
    name: "Chris Spelson",
    title: "Property Strategist – Equity & Refinance",
    location: "Sydney, NSW",
    yearsExperience: 9,
    bio: "Chris specialises in helping existing homeowners and investors identify and release equity to fund their next purchase — without disrupting their current position. He works closely with the finance brokers to structure refinances that open new borrowing capacity while maintaining a comfortable buffer. Chris has particular experience with clients making the transition from first home to investment portfolio.",
    rating: 4.9,
    reviewCount: 31,
    specialisations: ["Equity release strategies", "Refinance structuring", "First home to investment conversion", "Portfolio staging", "Cross-collateralisation risk management"],
    credentials: ["Cert IV in Property Services", "Diploma of Finance & Mortgage Broking", "One Group Certified Property Strategist"],
    bookingUrl: "/property/consultation",
    email: "chris.spelson@onegroup.com.au",
    stats: { portfoliosBuilt: 53, avgEquityGain: 165000, marketsCovered: 6 },
    testimonials: [
      {
        id: "t1",
        initials: "AP",
        quote: "Chris found equity in our home we didn't know we could access, and used it to fund our second property without any added stress. (placeholder)",
        attribution: "Amanda P., Sydney",
      },
      {
        id: "t2",
        initials: "RH",
        quote: "Clear, methodical, and honest about the risks. Exactly what we needed for our first investment move. (placeholder)",
        attribution: "Rob H., Newcastle",
      },
    ],
  },
  {
    id: "andrew-majdlik",
    name: "Andrew Majdlik",
    title: "Property Strategist – Commercial & SMSF",
    location: "Melbourne, VIC",
    yearsExperience: 14,
    bio: "Andrew focuses on commercial property investment and SMSF-held acquisitions for clients in the mid-to-late career stage looking to accelerate their super balance. He has deep expertise in limited recourse borrowing arrangements (LRBAs), commercial lease structuring, and the integration of solar assets within SMSF-compliant environments. Andrew coordinates with the finance team to ensure LRBA facilities are correctly structured from day one.",
    rating: 5.0,
    reviewCount: 22,
    specialisations: ["SMSF property acquisition", "Limited recourse borrowing (LRBA)", "Commercial lease structuring", "Industrial and retail investment", "Solar asset integration within SMSF"],
    credentials: ["Bachelor of Commerce (Finance)", "Cert IV in Property Services", "Registered Tax Agent (SMSF Planning)", "One Group Certified Property Strategist"],
    bookingUrl: "/property/consultation",
    email: "andrew.majdlik@onegroup.com.au",
    stats: { portfoliosBuilt: 78, avgEquityGain: 224000, marketsCovered: 5 },
    testimonials: [
      {
        id: "t1",
        initials: "LW",
        quote: "Andrew's grasp of SMSF compliance gave us the confidence to move our super into property. The LRBA setup was seamless. (placeholder)",
        attribution: "Lisa W., Melbourne",
      },
    ],
  },
];

const formatAvgEquityGain = (value: number) => `$${Math.round(value / 1000)}k`;

const getInitials = (name: string) =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box bg="gray.50" borderRadius="xl" p={4} textAlign="center">
    <Text fontSize="2xl" fontWeight="black" color={NAVY}>{value}</Text>
    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">{label}</Text>
  </Box>
);

const TestimonialItem: React.FC<{ testimonial: Testimonial }> = ({ testimonial: t }) => (
  <HStack align="start" spacing={3}>
    <Avatar size="sm" name={t.initials} bg={PROPERTY_PRIMARY} color="white" fontWeight="bold" fontSize="xs" />
    <Box>
      <Text fontSize="sm" color="gray.700" lineHeight="1.6">&ldquo;{t.quote}&rdquo;</Text>
      <Text fontSize="xs" color="gray.500" mt={1}>{t.attribution}</Text>
    </Box>
  </HStack>
);

const StrategistRow: React.FC<{ strategist: Strategist; isSelected: boolean; onClick: () => void }> = ({
  strategist: s,
  isSelected,
  onClick,
}) => (
  <Box
    as="button"
    onClick={onClick}
    aria-pressed={isSelected}
    display="flex"
    alignItems="flex-start"
    gap={3}
    w="full"
    p={3}
    textAlign="left"
    bg={isSelected ? "#F0FAF7" : "white"}
    border="1px solid"
    borderColor={isSelected ? PROPERTY_PRIMARY : "gray.100"}
    borderRadius="lg"
    boxShadow={isSelected ? "0 0 0 1px " + PROPERTY_PRIMARY : "sm"}
    transition="all 0.15s"
    _hover={{ borderColor: PROPERTY_PRIMARY, bg: "#F6FDFB" }}
  >
    <Avatar size="md" name={s.name} src={s.photoUrl} bg={NAVY} color="white" fontWeight="bold" flexShrink={0} />
    <Box flex={1} minW={0}>
      <Flex justify="space-between" align="baseline" gap={2}>
        <Text fontSize="sm" fontWeight="600" color={NAVY} noOfLines={1}>{s.name}</Text>
        <HStack spacing={1} flexShrink={0} color="gray.500">
          <FaStar size={10} color="#F2B705" />
          <Text fontSize="xs" whiteSpace="nowrap">{s.rating.toFixed(1)} · {s.reviewCount}</Text>
        </HStack>
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={2} noOfLines={1}>
        {s.title} · {s.location} · {s.yearsExperience} yrs
      </Text>
      <HStack spacing={1} flexWrap="wrap">
        {s.specialisations.slice(0, 2).map((tag) => (
          <Tag key={tag} size="sm" bg="#E1F5EE" color="#0F6E56" fontSize="10px" borderRadius="md">{tag}</Tag>
        ))}
      </HStack>
    </Box>
  </Box>
);

const ProfilePanel: React.FC<{ strategist: Strategist; onClose: () => void }> = ({ strategist: s, onClose }) => (
  <Box
    aria-label={`Profile: ${s.name}`}
    bg="white"
    h="full"
    overflowY="auto"
    position="relative"
    borderRadius={{ base: "16px 16px 0 0", md: 0 }}
  >
    <IconButton
      aria-label="Close profile"
      icon={<FaTimes />}
      size="sm"
      variant="outline"
      borderRadius="full"
      position="absolute"
      top={4}
      right={4}
      onClick={onClose}
    />

    <Container maxW="container.md" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Flex gap={5} align="flex-start" wrap="wrap">
          <Avatar size="2xl" name={s.name} src={s.photoUrl} bg={NAVY} color="white" fontWeight="bold" fontSize="2xl" />
          <Box flex={1} minW="200px">
            <Flex justify="space-between" align="baseline" wrap="wrap" gap={2}>
              <Heading size="lg" color={NAVY}>{s.name}</Heading>
              <HStack spacing={1} color="gray.500">
                <FaStar size={12} color="#F2B705" />
                <Text fontSize="sm">{s.rating.toFixed(1)} · {s.reviewCount} reviews</Text>
              </HStack>
            </Flex>
            <Text fontSize="sm" color={PROPERTY_PRIMARY} fontWeight="600">{s.title}</Text>
            <HStack spacing={1} color="gray.500" mt={1}>
              <FaMapMarkerAlt size={12} />
              <Text fontSize="xs">{s.location} · {s.yearsExperience} yrs · {s.credentials.join(" · ")}</Text>
            </HStack>
            <HStack spacing={1} flexWrap="wrap" mt={3}>
              {s.specialisations.map((tag) => (
                <Tag key={tag} size="sm" bg="#E1F5EE" color="#0F6E56" borderRadius="md">{tag}</Tag>
              ))}
            </HStack>
            <HStack spacing={3} mt={4} flexWrap="wrap">
              {s.bookingUrl && (
                <Button as={RouterLink} to={s.bookingUrl} bg={PROPERTY_PRIMARY} color="white" _hover={{ bg: "#0a6459" }} rightIcon={<FaArrowRight />} size="sm">
                  Book with {s.name.split(" ")[0]}
                </Button>
              )}
              {s.email && (
                <Button as="a" href={`mailto:${s.email}`} variant="outline" borderColor="gray.300" color="gray.600" leftIcon={<FaEnvelope />} size="sm">
                  Message
                </Button>
              )}
            </HStack>
          </Box>
        </Flex>

        <Divider />

        {/* Bio */}
        <Text color="gray.700" lineHeight="1.8" fontSize="md">{s.bio}</Text>

        {/* Stats */}
        <Box>
          <Heading size="sm" color={NAVY} mb={3}>Key Stats</Heading>
          <SimpleGrid columns={3} spacing={4}>
            <StatCard label="Portfolios Built" value={`${s.stats.portfoliosBuilt}+`} />
            <StatCard label="Avg. Equity Gain" value={formatAvgEquityGain(s.stats.avgEquityGain)} />
            <StatCard label="Markets Covered" value={String(s.stats.marketsCovered)} />
          </SimpleGrid>
        </Box>

        {/* Testimonials */}
        {s.testimonials.length > 0 && (
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Client Wins</Heading>
            <VStack align="stretch" spacing={4}>
              {s.testimonials.map((t) => (
                <TestimonialItem key={t.id} testimonial={t} />
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  </Box>
);

const PropertyStrategists: React.FC = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = STRATEGISTS.find((s) => s.id === selectedId) ?? null;

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/strategists"
        fallbackTitle="Our Property Strategists | One Property Australia"
        fallbackDescription="Browse our property investment strategists and find the right fit for your portfolio goals."
      />
      <NavbarWithCallToAction />

      {/* Header */}
      <Box bg="gray.50" borderBottom="1px solid" borderColor="gray.100" py={8}>
        <Container maxW="container.xl">
          <Heading size="xl" color={NAVY}>Our Property Strategists</Heading>
          <Text color="gray.600" mt={2} maxW="2xl">
            Browse our team and select a strategist to see their full profile, track record, and client results.
          </Text>
        </Container>
      </Box>

      {/* Directory */}
      <Flex
        position="relative"
        h={{ base: "auto", md: "calc(100vh - 220px)" }}
        direction={{ base: "column", md: "row" }}
        overflow="hidden"
      >
        {/* List column */}
        <Box
          w={{ base: "full", md: "360px" }}
          flexShrink={0}
          borderRight={{ base: "none", md: "1px solid" }}
          borderColor="gray.100"
          overflowY="auto"
          p={4}
        >
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={3}>
            {STRATEGISTS.length} strategists
          </Text>
          <VStack spacing={3} align="stretch">
            {STRATEGISTS.map((s) => (
              <StrategistRow
                key={s.id}
                strategist={s}
                isSelected={s.id === selectedId}
                onClick={() => setSelectedId(s.id)}
              />
            ))}
          </VStack>
        </Box>

        {/* Profile panel — desktop: inline column; mobile: bottom sheet overlay */}
        {selected && (
          <Box
            display={{ base: "block", md: "block" }}
            position={{ base: "fixed", md: "static" }}
            top={{ base: 0, md: "auto" }}
            left={{ base: 0, md: "auto" }}
            right={{ base: 0, md: "auto" }}
            bottom={{ base: 0, md: "auto" }}
            zIndex={{ base: "overlay", md: "auto" }}
            h={{ base: "85vh", md: "full" }}
            mt={{ base: "15vh", md: 0 }}
            flex={{ base: "none", md: 1 }}
            boxShadow={{ base: "0 -8px 24px rgba(0,0,0,0.15)", md: "none" }}
          >
            <ProfilePanel strategist={selected} onClose={() => setSelectedId(null)} />
          </Box>
        )}
      </Flex>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyStrategists;
